import { useGridApiRef } from "@mui/x-data-grid-pro";
import moment from "moment";
import { createContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ShiftCell } from "./ShiftCell";
import { OvertimeCell } from "./OvertimeCell";
import { EditPunchCell, PunchCell } from "./PunchCell";
import { ExceptionCell } from "./ExceptionCell";
import { WorkedHoursCell } from "./WorkedHoursCell";
import { PaycodeCell } from "./PaycodeCell";
import { gettingTags } from "utils/Filter";
import { daysBetween, getMonth } from "utils/Times";
import {
	gettingAssingedPaycodes,
	gettingPunchesAndExceptions,
	gettingSchedule,
} from "utils/Timecard";
import {
	buildTimecardPath,
	buildTimecardSearch,
	parseTimecardRoute,
} from "utils/timecardRouting";

const Context = createContext({});

const ContextProvider = ({ children, value }) => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();

	const apiRef = useGridApiRef();

	const [virtualization, setVirtualization] = useState(false);
	const [virtualTotals, setVirtualTotals] = useState(false);
	const [rows, setRows] = useState([]);

	const [tags, setTags] = useState([]);
	const [tag, setTag] = useState([]);
	const [filterTags, setFilterTags] = useState("");
	const [loadingGettingTags, setLoadingGettingTags] = useState(false);

	const [range, setRange] = useState([
		moment(getMonth().start_date),
		moment(getMonth().end_date),
	]);
	const [totalsRange, setTotalsRange] = useState([
		moment(getMonth().start_date),
		moment(getMonth().end_date),
	]);
	const [names, setNames] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [employee, setEmployee] = useState([]);
	const [pendingEmployeeId, setPendingEmployeeId] = useState(null);
	const [urlHydrated, setUrlHydrated] = useState(false);
	const [filterEmployees, setFilterEmployees] = useState("");
	const [cell, setCell] = useState({ column: null, row: null });
	const [contextMenu, setContextMenu] = useState(null);

	const [openApprove, setOpenApprove] = useState(false);
	const [openRecant, setOpenRecant] = useState(false);

	const [openAssignPaycode, setOpenAssignPaycode] = useState(false);
	const [openTotals, setOpenTotals] = useState(false);

	const [deleteRealPunches, setDeleteRealPunches] = useState([]);

	const columns = useMemo(
		() => [
			{
				field: "date",
				headerName: t("timekeeper.timecard.table.columns.date"),
				width: 100,
				minWidth: 100,
				align: "center",
				valueFormatter: (params) => {
					const str = moment(params, "YYYY-MM-DD").format("ddd DD");

					return str.charAt(0).toUpperCase() + str.slice(1);
				},
				//filterOperators: getGridStringOperators().filter(operator => operator.value !== 'isAnyOf'),
			},
			{
				field: "overtime",
				headerName: t("timekeeper.timecard.table.columns.overtimes"),
				minWidth: 120,
				width: 120,
				editable: false,
				renderCell: (params) => (
					<OvertimeCell
						params={params}
						setOpenApprove={setOpenApprove}
						setOpenRecant={setOpenRecant}
					/>
				),
			},
			{
				field: "shifts",
				headerName: t("timekeeper.timecard.table.columns.shifts"),
				minWidth: 185,
				width: 185,
				editable: false,
				renderCell: (params) => <ShiftCell params={params} />,
			},
			{
				field: "punch_in",
				headerName: t("timekeeper.timecard.table.columns.punch_in"),
				flex: 1,
				minWidth: 205,
				width: 205,
				editable: true,
				renderCell: (params) => (
					<PunchCell
						params={params}
						//setAddOpenPaycode={setAddOpenPaycode}
						//setAction={setAction}
						//setCell={setCell}
						setAddOpenPaycode={() => {}}
						setAction={() => {}}
						setCell={() => {}}
					/>
				),
				renderEditCell: (params) => <EditPunchCell params={params} />,
			},
			{
				field: "punch_out",
				headerName: t("timekeeper.timecard.table.columns.punch_out"),
				flex: 1,
				minWidth: 205,
				width: 205,
				editable: true,
				renderCell: (params) => (
					<PunchCell
						params={params}
						//setAddOpenPaycode={setAddOpenPaycode}
						//setAction={setAction}
						//setCell={setCell}
						setAddOpenPaycode={() => {}}
						setAction={() => {}}
						setCell={() => {}}
					/>
				),
				renderEditCell: (params) => <EditPunchCell params={params} />,
			},
			{
				field: "exceptions",
				headerName: t("timekeeper.timecard.table.columns.exceptions"),
				flex: 1,
				minWidth: 245,
				width: 245,
				editable: false,
				renderCell: (params) => (
					<ExceptionCell
						params={params}
						/* setAddOpenPaycode={setAddOpenPaycode}
						setAction={setAction}
						setCell={setCell} */
						setAddOpenPaycode={() => {}}
						setAction={() => {}}
						setCell={() => {}}
					/>
				),
			},
			{
				field: "paycodes",
				headerName: t("timekeeper.timecard.table.columns.paycodes"),
				minWidth: 205,
				width: 205,
				editable: true,
				renderCell: (params) => <PaycodeCell params={params} />,
				//renderEditCell: (params) => <PaycodeEditCell params={params} />,
			},
			{
				field: "worked_hours",
				headerName: t("timekeeper.timecard.table.columns.total_hours"),
				minWidth: 120,
				width: 120,
				align: "center",
				editable: false,
				renderCell: (params) => <WorkedHoursCell params={params} />,
			},
		],
		[t]
	);

	const [loadingGettingSchedule, setLoadingGettingSchedule] = useState(false);
	const [
		loadingGettingPunchesAndExceptions,
		setLoadingGettingPunchesAndExceptions,
	] = useState(false);

	const [loadingGettingPaycodes, setLoadingGettingPaycodes] = useState(false);

	const reloading = async () => {
		let preRows = [];
		setDeleteRealPunches([]);

		for (let day of daysBetween(range[0], range[1])) {
			preRows.push({
				id: `${employee[0]?.id}-${day}`,
				employee_id: employee[0]?.id,
				date: day,
				open_period: {},
			});
		}

		setRows(preRows);

		await gettingSchedule(
			setLoadingGettingSchedule,
			setRows,
			employee,
			range
		);

		await gettingPunchesAndExceptions(
			setLoadingGettingPunchesAndExceptions,
			employee,
			range,
			setRows
		);

		await gettingAssingedPaycodes(
			setLoadingGettingPaycodes,
			employee,
			range,
			setRows
		);

		// obtener los turnos
		/*
		shifts: [
			{
				"date": "2025-03-31",
				"start": "08:00",
				"end": "16:30",
				"segments": [
					{
						"locked": "false",
						"person_number": "20852479-8",
						"type": "REGULAR",
						"org_job_path": "",
						"date": "2025-03-31",
						"start": "2025-03-31T08:00:00",
						"end": "2025-03-31T16:30:00",
						"amount": 8
					}
				]
			}
		]
		*/
	};

	useEffect(() => {
		gettingTags(
			setLoadingGettingTags,
			setTags,
			setTag,
			setEmployees,
			setEmployee,
			() => {},
			"timecard",
			{
				start_date: range[0].format("YYYY-MM-DD"),
				end_date: range[1].format("YYYY-MM-DD"),
				without_hiring: false,
			}
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const parsed = parseTimecardRoute(location.pathname, location.search);

		if (parsed.employeeId !== null) {
			setPendingEmployeeId(parsed.employeeId);
		}

		if (parsed.from && parsed.to) {
			setRange([
				moment(parsed.from, "YYYY-MM-DD"),
				moment(parsed.to, "YYYY-MM-DD"),
			]);
		}

		if (parsed.totalsFrom && parsed.totalsTo) {
			setTotalsRange([
				moment(parsed.totalsFrom, "YYYY-MM-DD"),
				moment(parsed.totalsTo, "YYYY-MM-DD"),
			]);
		}

		setOpenTotals(parsed.showTotals);
		setOpenAssignPaycode(parsed.assignPaycode);
		setUrlHydrated(true);
	}, [location.pathname, location.search]);

	useEffect(() => {
		if (pendingEmployeeId === null || employees.length === 0) {
			return;
		}

		const matched = employees.find(
			(emp) => Number(emp?.id) === Number(pendingEmployeeId)
		);

		if (!matched) {
			return;
		}

		if (Number(employee[0]?.id) !== Number(matched.id)) {
			setEmployee([matched]);
		}

		setPendingEmployeeId(null);
	}, [employee, employees, pendingEmployeeId]);

	useEffect(() => {
		if (!openAssignPaycode || cell?.row || !range[0]?.isValid?.()) {
			return;
		}

		setCell((prev) => ({
			...prev,
			row: range[0].format("YYYY-MM-DD"),
			employee_id: employee.length > 0 ? employee[0]?.id : undefined,
		}));
	}, [cell?.row, employee, openAssignPaycode, range]);

	useEffect(() => {
		if (!urlHydrated) {
			return;
		}

		const employeeIdForPath =
			pendingEmployeeId !== null
				? pendingEmployeeId
				: employee.length > 0
				  ? employee[0]?.id
				  : null;

		const from =
			range[0] && range[0].isValid()
				? range[0].format("YYYY-MM-DD")
				: null;
		const to =
			range[1] && range[1].isValid()
				? range[1].format("YYYY-MM-DD")
				: null;

		const totalsFrom =
			totalsRange[0] && totalsRange[0].isValid()
				? totalsRange[0].format("YYYY-MM-DD")
				: null;
		const totalsTo =
			totalsRange[1] && totalsRange[1].isValid()
				? totalsRange[1].format("YYYY-MM-DD")
				: null;

		const targetPath = buildTimecardPath(employeeIdForPath);
		const targetSearch = buildTimecardSearch({
			from,
			to,
			showTotals: openTotals,
			totalsFrom,
			totalsTo,
			assignPaycode: openAssignPaycode,
		});

		if (
			location.pathname !== targetPath ||
			location.search !== targetSearch
		) {
			navigate(`${targetPath}${targetSearch}`, { replace: true });
		}
	}, [
		employee,
		location.pathname,
		location.search,
		navigate,
		openAssignPaycode,
		openTotals,
		pendingEmployeeId,
		range,
		totalsRange,
		urlHydrated,
	]);

	return (
		<Context.Provider
			value={{
				...value,
				rows,
				setRows,
				columns,
				virtualization,
				setVirtualization,
				virtualTotals,
				setVirtualTotals,
				apiRef,
				openApprove,
				setOpenApprove,
				openRecant,
				setOpenRecant,
				openAssignPaycode,
				setOpenAssignPaycode,
				openTotals,
				setOpenTotals,
				tableDateRange: range,
				setTableDateRange: setRange,
				totalsDateRange: totalsRange,
				setTotalsDateRange: setTotalsRange,
				names,
				setNames,
				employees,
				setEmployees,
				employee,
				setEmployee,
				filterEmployees,
				setFilterEmployees,
				tags,
				setTags,
				tag,
				setTag,
				filterTags,
				setFilterTags,
				cell,
				setCell,
				deleteRealPunches,
				setDeleteRealPunches,
				contextMenu,
				setContextMenu,
				loadingGettingTags,
				setLoadingGettingTags,
				loadingGettingSchedule,
				setLoadingGettingSchedule,
				loadingGettingPunchesAndExceptions,
				setLoadingGettingPunchesAndExceptions,
				loadingGettingPaycodes,
				setLoadingGettingPaycodes,
				reloading,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export { Context, ContextProvider };
